import React, {lazy, Suspense, useEffect, useState} from "react";
import {Flex, Grid, GridItem, Skeleton} from "@chakra-ui/react";
import ControlTab from "../Dashboard/components/ControlTab";
import Card from "../../../components/Card/Card";
import DigitalBellRemote from "../../../components/Bell/Remote";
import BellStatCards from "./components/BellStatCards";
import {fetchAppAdvertisements} from "../../../Services/AdvertisementService";

// Lazily load the carousel so its code only ships when there are ads to show.
const AdvertisementSlider = lazy(() => import("./components/AdvertisementSlider"));

export default function BellDashboard() {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));

    // "loading" while fetching, "ready" with ads, "hidden" on failure / no ads.
    const [adState, setAdState] = useState("loading");
    const [ads, setAds] = useState([]);
    const [adSpeed, setAdSpeed] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const {ads: fetched, speed} = await fetchAppAdvertisements();
                if (!active) return;
                if (fetched.length > 0) {
                    setAds(fetched);
                    setAdSpeed(speed);
                    setAdState("ready");
                } else {
                    // No ads to show → hide the section entirely.
                    setAdState("hidden");
                }
            } catch (e) {
                // Failed to load → hide the advertisement section on this page.
                if (active) setAdState("hidden");
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    return (
        <Flex flexDirection="column" pt={{base: "82px", md: "88px", xl: "75px"}} minW="0" gap={{base: 4, md: 6}}>
            <Grid
                templateColumns={{
                    base: "minmax(0, 1fr)",
                    lg: "minmax(0, 1.5fr) minmax(320px, 1fr)",
                    xl: "minmax(0, 1.6fr) minmax(380px, 1fr)",
                }}
                gap={{base: 3, md: 3, xl: 4}}
                alignItems="start"
            >
                <GridItem minW="0">
                    <Card p={{base: "0.5rem", md: "1rem"}} minW="0">
                        <ControlTab/>
                    </Card>
                </GridItem>
                <GridItem minW="0">
                    <Flex direction="column" gap={{base: 4, md: 6}}>
                        {/* Advertisement carousel — shown only once ads load
                            successfully; hidden on fetch failure or when empty. */}
                        {adState === "loading" && (
                            <Skeleton height={{base: "210px", md: "230px", xl: "245px"}} borderRadius="16px"/>
                        )}
                        {adState === "ready" && (
                            <Suspense fallback={<Skeleton height={{base: "210px", md: "230px", xl: "245px"}} borderRadius="16px"/>}>
                                <AdvertisementSlider ads={ads} speed={adSpeed}/>
                            </Suspense>
                        )}
                        {userInfo?.isRemoteOn === "Y" && (
                            <Card p={{base: "0.75rem", md: "1.2rem"}} minW="0">
                                <DigitalBellRemote/>
                            </Card>
                        )}
                        <BellStatCards/>
                    </Flex>
                </GridItem>
            </Grid>
        </Flex>
    );
}
